import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Edit2, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { COUNTRIES } from "@/lib/countries";
import { greetingFor } from "@/lib/salutation";

export default function Customers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | customer object
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/customers");
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.address_city?.toLowerCase().includes(q)
    );
  });

  const remove = async (id) => {
    if (!window.confirm("Diesen Kunden löschen?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="crm-customers">
      <div className="mb-5 md:mb-8 flex items-start justify-between gap-3 md:gap-4 flex-wrap">
        <div>
          <div className="hidden md:block text-xs font-medium text-[#6B7F3E] uppercase tracking-wider mb-2">CRM</div>
          <h1 className="hidden md:block font-display text-3xl font-semibold text-[#1A201A] tracking-tight">Kunden</h1>
          <p className="text-sm text-[#5C6B5D] mt-1">Kundenstammdaten und Objektzuordnung.</p>
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <Input
            placeholder="Suchen…" value={query} onChange={(e) => setQuery(e.target.value)}
            className="flex-1 md:flex-none md:w-56" data-testid="customer-search-input"
          />
          <Button className="bg-[#6B7F3E] hover:bg-[#5A6B34]" onClick={() => setEditing("new")} data-testid="customer-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Neu
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        {loading && <div className="p-8 text-sm text-[#5C6B5D]">Lädt…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-16 text-center text-sm text-[#5C6B5D]">Keine Kunden gefunden.</div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="crm-table w-full text-sm">
              <thead className="bg-[#F5F6F0] text-[11px] uppercase tracking-wider text-[#5C6B5D]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Name</th>
                  <th className="text-left px-4 py-3 font-medium">Kontakt</th>
                  <th className="text-left px-4 py-3 font-medium">Adresse</th>
                  <th className="text-left px-4 py-3 font-medium">Typ</th>
                  <th className="text-right px-4 py-3 font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-t border-[#E5E7EB] hover:bg-[#FAFAF5]">
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1A201A]">{c.name}</div>
                      {c.objekt_art === "gewerbe" && c.ansprechpartner && (
                        <div className="text-xs text-[#5C6B5D]">
                          Ansprechperson: {c.anrede === "herr" ? "Herr " : c.anrede === "frau" ? "Frau " : ""}{c.ansprechpartner}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#4A5D4E] crm-td-stack" data-label="Kontakt">
                      <div>{c.email || "—"}</div>
                      <div className="text-[#5C6B5D]">{c.phone || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#4A5D4E] crm-td-stack" data-label="Adresse">
                      <div>{c.address_street || "—"}</div>
                      <div className="text-[#5C6B5D]">{c.address_zip} {c.address_city}</div>
                    </td>
                    <td className="px-4 py-3" data-label="Typ">
                      <Badge variant="secondary" className="capitalize">{c.objekt_art}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right crm-td-actions">
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" onClick={() => setEditing(c)} data-testid={`customer-edit-${c.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`customer-delete-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-[#B85C38]" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CustomerDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        customer={editing === "new" ? null : editing}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function CustomerDialog({ open, onOpenChange, customer, onSaved }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address_street: "", address_zip: "",
    address_city: "", address_country: "Deutschland", objekt_art: "privat",
    anrede: "", ansprechpartner: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: customer?.name || "",
        email: customer?.email || "",
        phone: customer?.phone || "",
        address_street: customer?.address_street || "",
        address_zip: customer?.address_zip || "",
        address_city: customer?.address_city || "",
        address_country: customer?.address_country || "Deutschland",
        objekt_art: customer?.objekt_art || "privat",
        anrede: customer?.anrede || "",
        ansprechpartner: customer?.ansprechpartner || "",
        notes: customer?.notes || "",
      });
    }
  }, [open, customer]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isGewerbe = form.objekt_art === "gewerbe";

  const save = async () => {
    if (!form.name) { toast.error(isGewerbe ? "Firmenname ist Pflicht" : "Name ist Pflicht"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        email: form.email || null,
        phone: form.phone || null,
        anrede: form.anrede || null,
        ansprechpartner: isGewerbe ? (form.ansprechpartner || null) : null,
      };
      if (customer) await api.put(`/customers/${customer.id}`, body);
      else await api.post("/customers", body);
      toast.success(customer ? "Gespeichert" : "Kunde angelegt – keine E-Mail versendet");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-display">{customer ? "Kunde bearbeiten" : "Neuer Kunde"}</DialogTitle>
        </DialogHeader>
        {!customer && (
          <div className="flex items-start gap-2 text-xs text-[#4A5D4E] bg-[#F5F6F0] border border-[#E5E7EB] rounded-lg px-3 py-2">
            <Info className="w-4 h-4 mt-0.5 text-[#6B7F3E] shrink-0" />
            <div>
              Manueller Eintrag – es wird <b>keine Terminbestätigung</b> oder
              automatische E-Mail an den Kunden versendet.
            </div>
          </div>
        )}
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Objektart</Label>
              <Select value={form.objekt_art} onValueChange={(v) => update("objekt_art", v)}>
                <SelectTrigger className="mt-1.5" data-testid="customer-objektart-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="privat">Privat</SelectItem>
                  <SelectItem value="gewerbe">Gewerbe</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>{isGewerbe ? "Firmenname *" : "Name *"}</Label>
              <Input className="mt-1.5" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="customer-name-input" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Anrede</Label>
              <Select value={form.anrede} onValueChange={(v) => update("anrede", v)}>
                <SelectTrigger className="mt-1.5" data-testid="customer-anrede-select">
                  <SelectValue placeholder="Bitte wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="frau">Frau</SelectItem>
                  <SelectItem value="herr">Herr</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isGewerbe ? (
              <div className="col-span-2">
                <Label>Ansprechperson</Label>
                <Input className="mt-1.5" placeholder="Vor- und Nachname" value={form.ansprechpartner} onChange={(e) => update("ansprechpartner", e.target.value)} data-testid="customer-ansprechpartner-input" />
              </div>
            ) : (
              <div className="col-span-2 self-end pb-2 text-xs text-[#5C6B5D]" data-testid="customer-greeting-preview">
                {greetingFor({ ...form, anrede: form.anrede || null })}
              </div>
            )}
          </div>
          {isGewerbe && (
            <div className="text-xs text-[#5C6B5D] -mt-1" data-testid="customer-greeting-preview">
              {greetingFor({ ...form, anrede: form.anrede || null })} – Anschrift in Angebot/Rechnung bleibt die Firmenanschrift.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>E-Mail (optional)</Label>
              <Input type="email" className="mt-1.5" placeholder="Kann später ergänzt werden" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="customer-email-input" />
            </div>
            <div>
              <Label>Telefon</Label>
              <Input className="mt-1.5" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="customer-phone-input" />
            </div>
          </div>
          <div>
            <Label>Straße</Label>
            <Input className="mt-1.5" value={form.address_street} onChange={(e) => update("address_street", e.target.value)} data-testid="customer-street-input" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>PLZ</Label>
              <Input className="mt-1.5" value={form.address_zip} onChange={(e) => update("address_zip", e.target.value)} data-testid="customer-zip-input" />
            </div>
            <div className="col-span-2">
              <Label>Ort</Label>
              <Input className="mt-1.5" value={form.address_city} onChange={(e) => update("address_city", e.target.value)} data-testid="customer-city-input" />
            </div>
          </div>
          <div>
            <div>
              <Label>Land</Label>
              <Select value={form.address_country} onValueChange={(v) => update("address_country", v)}>
                <SelectTrigger className="mt-1.5" data-testid="customer-country-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.name} data-testid={`customer-country-option-${c.code}`}>
                      <span className="mr-2">{c.flag}</span>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Notizen</Label>
            <Textarea className="mt-1.5" rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="customer-notes-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="bg-[#6B7F3E] hover:bg-[#5A6B34]" onClick={save} disabled={saving} data-testid="customer-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
